/* =====================================================================
   CUSTOM: Mobile "pill" navigation — behavior helper
   ---------------------------------------------------------------------
   1. Measures the pill bar's height and exposes it as --pill-bar-height
      so the submenu panel can sit right below the pills.
   2. Auto-opens the first top-level item (that has a submenu) when the
      mobile menu opens, so there is always content on screen.

   Scope: mobile only (< 1024px). Does nothing on desktop.
   Paired with: custom-mobile-pills.css
   ===================================================================== */
(function () {
  var LG_BREAKPOINT = 1024;

  // Second-level section to auto-expand when the mobile menu opens.
  // Must match the menu item's name exactly (case-insensitive). Leave '' to disable.
  var EXPAND_SECTION_TITLE = 'Bli sipas llojit të produktit';

  function isMobile() {
    return window.innerWidth < LG_BREAKPOINT;
  }

  function init() {
    var menu = document.querySelector('main-menu');
    if (!menu) return;

    var disclosure = menu.querySelector('.main-menu__disclosure');
    var nav = menu.querySelector('.main-nav');
    if (!disclosure || !nav) return;

    // Tracks whether the user manually closed the default section this session
    // (so we don't keep forcing it back open). Reset when the drawer closes.
    var defaultManuallyClosed = false;
    var suppressDefaultTracking = false;
    var panelsRelocated = false;

    function setPillBarHeight() {
      if (!isMobile()) return;
      var h = nav.offsetHeight;
      if (h) menu.style.setProperty('--pill-bar-height', h + 'px');
      // Measure the social bar too, so the submenu panels can leave exactly that
      // much room at the bottom and the icons stay pinned/visible while scrolling.
      var social = menu.querySelector('.main-nav__social');
      menu.style.setProperty('--social-bar-height', (social ? social.offsetHeight : 0) + 'px');
    }

    // iOS Safari HIDES position:fixed elements that live inside a horizontally
    // scrollable overflow container. The theme puts each top-level submenu panel
    // (.main-nav__child) inside .main-nav — which we turn into the scrolling pill
    // bar — so once the pills overflow, every panel goes blank on real iPhones.
    // Fix: move the panels OUT of .main-nav (into the non-scrolling drawer body)
    // and drive their visibility ourselves via an `is-shown` class that mirrors
    // each pill's <details> open state. Mobile only — desktop keeps them in place.
    function syncPanel(details) {
      var panel = details._panel;
      if (!panel) return;
      var show = details.classList.contains('is-open')
          && !details.classList.contains('is-closing');
      panel.classList.toggle('is-shown', show);
    }

    function relocatePanels() {
      if (panelsRelocated || !isMobile()) return;
      var host = menu.querySelector('.main-menu__content');
      if (!host) return;
      var moved = false;
      nav.querySelectorAll(':scope > li > details').forEach(function (details) {
        var panel = details.querySelector(':scope > .main-nav__child');
        if (!panel) return;
        details._panel = panel;
        host.appendChild(panel);
        syncPanel(details);
        new MutationObserver(function () { syncPanel(details); })
            .observe(details, { attributes: true, attributeFilter: ['class'] });
        moved = true;
      });
      if (!moved) return;
      panelsRelocated = true;
      // The theme delegates submenu clicks from .main-nav; the relocated panels
      // now live outside it, so toggle their 2nd-level accordions here instead.
      host.addEventListener('click', handlePanelClick);
    }

    // Open/close a 2nd-level section (e.g. Protokollet, Tipologji) inside a
    // relocated panel, and remember if the user manually closed the default one.
    // The whole row must toggle: the heading is a separate <a> link and the
    // <summary> is only the ± icon, so we handle BOTH (matching the theme, which
    // also toggles a row's sibling <details> when its parent link is tapped).
    function handlePanelClick(evt) {
      if (!isMobile() || !evt.target.closest) return;
      if (!evt.target.closest('.main-nav__child')) return;

      var details;
      var summary = evt.target.closest('summary.main-nav__summary');
      if (summary) {
        details = summary.closest('details');
      } else {
        var link = evt.target.closest('.main-nav__item');
        if (!link || !link.parentElement) return;
        details = link.parentElement.querySelector(':scope > details');
        if (!details) return; // a real leaf link — let it navigate
      }
      if (!details) return;
      evt.preventDefault();
      evt.stopPropagation();

      if (details.classList.contains('is-open')) {
        if (typeof menu.close === 'function') menu.close(details, true);
      } else if (typeof menu.open === 'function') {
        menu.open(details);
      }

      if (EXPAND_SECTION_TITLE && !suppressDefaultTracking) {
        var li = details.parentElement;
        var heading = li && li.querySelector('.main-nav__heading');
        if (heading && heading.textContent.trim().toLowerCase()
            === EXPAND_SECTION_TITLE.trim().toLowerCase()) {
          setTimeout(function () {
            defaultManuallyClosed = !details.classList.contains('is-open');
          }, 400);
        }
      }
    }

    // Keep the default section (e.g. Tipologji produkti) expanded, AND expand
    // the section that holds the current page — both, in the open pill.
    function expandRelevantSection() {
      if (!isMobile()) return;
      var openDetails = nav.querySelector(':scope > li > details.is-open');
      if (!openDetails) return;
      // The panel was relocated out of the <details>, so reach it via the link
      // we stored (falls back to the in-place child on desktop).
      var openPanel = openDetails._panel
          || openDetails.querySelector(':scope > .main-nav__child');
      if (!openPanel) return;

      // 1. Default section stays expanded — unless the user closed it manually.
      if (EXPAND_SECTION_TITLE && !defaultManuallyClosed) {
        var target = EXPAND_SECTION_TITLE.trim().toLowerCase();
        openPanel.querySelectorAll('.child-nav > li:has(> details)').forEach(function (li) {
          var heading = li.querySelector('.main-nav__heading');
          var details = li.querySelector(':scope > details');
          if (!heading || !details) return;
          if (heading.textContent.trim().toLowerCase() === target
              && !details.classList.contains('is-open')) {
            var summary = details.querySelector(':scope > summary');
            if (summary) {
              suppressDefaultTracking = true;
              summary.click();
              suppressDefaultTracking = false;
            }
          }
        });
      }

      // 2. Also expand the section containing the current page.
      var current = openPanel.querySelector('[aria-current="page"]');
      if (current) {
        var grand = current.closest('.main-nav__grandchild');
        if (grand) {
          var d = grand.closest('details');
          if (d && !d.classList.contains('is-open')) {
            var s = d.querySelector(':scope > summary');
            if (s) s.click();
          }
        }
      }
    }

    function ensureFirstPillOpen() {
      if (!isMobile()) return;
      if (!disclosure.classList.contains('is-open')) return;

      // A pill already open? Just handle sections.
      if (nav.querySelector(':scope > li > details.is-open')) {
        expandRelevantSection();
        return;
      }

      // Open the COLUMNS menu (Skincare / Haircare) whose pill OR its submenu
      // holds the current page, so that section expands + the item highlights.
      // The panels are relocated OUT of .main-nav on mobile, so we search each
      // L1's stored `._panel` (not just the nav DOM — that's why this previously
      // only worked for Skincare, which is merely the default first pill).
      // Restricted to `data-nav-columns` items; Makeup (tabs) / Markat tona
      // (cards) / Rreth nesh are intentionally left out. Plain links (e.g. the
      // "Home" item) have no details and are never auto-clicked.
      var opened = false;
      var columnsLis = nav.querySelectorAll(':scope > li[data-nav-columns]');
      for (var i = 0; i < columnsLis.length && !opened; i += 1) {
        var li = columnsLis[i];
        var pill = li.querySelector(':scope > .main-nav__item--primary');
        var d = li.querySelector(':scope > details');
        var panel = d && (d._panel || d.querySelector(':scope > .main-nav__child'));
        var isCurrent = (pill && pill.matches('[aria-current="page"]'))
            || (panel && panel.querySelector('[aria-current="page"]'));
        if (isCurrent && pill) { pill.click(); opened = true; }
      }
      if (!opened) {
        var firstPill = nav.querySelector(
            ':scope > li:has(> details) > .main-nav__item--primary'
        );
        if (firstPill) firstPill.click(); // opens submenu (does not navigate on mobile)
      }
      setPillBarHeight();
      setTimeout(expandRelevantSection, 120); // after the panel has opened
    }

    // React whenever the drawer opens/closes.
    var observer = new MutationObserver(function () {
      if (disclosure.classList.contains('is-open')) {
        relocatePanels();
        setPillBarHeight();
        setTimeout(ensureFirstPillOpen, 50); // let layout settle first
      } else {
        defaultManuallyClosed = false; // fresh session next time
      }
    });
    observer.observe(disclosure, { attributes: true, attributeFilter: ['class'] });

    // Remember if the user manually collapses the default section (Tipologji…),
    // so we stop auto-reopening it until the drawer is closed and reopened.
    nav.addEventListener('click', function (evt) {
      if (suppressDefaultTracking || !EXPAND_SECTION_TITLE) return;
      var summary = evt.target.closest && evt.target.closest('summary.main-nav__summary');
      if (!summary) return;
      var li = summary.closest('.child-nav > li');
      var heading = li && li.querySelector('.main-nav__heading');
      if (!heading) return;
      if (heading.textContent.trim().toLowerCase() !== EXPAND_SECTION_TITLE.trim().toLowerCase()) return;
      var details = summary.closest('details');
      setTimeout(function () {
        defaultManuallyClosed = details && !details.classList.contains('is-open');
      }, 400);
    });

    // Same "reveal the next item" nudge as the protocol step tabs: when an item
    // near the right edge is tapped, scroll its row so the NEXT item peeks into
    // view — a hint that the row is scrollable. Used for BOTH the top-level pills
    // and the submenu collection-tabs. Uses rects (robust to whatever the
    // scroller's offset parent is) and scrolls the row only, never the page.
    var REVEAL_TRAIL = 24; // px peek of the item AFTER the revealed one
    function revealNextInScroller(scroller, item) {
      if (!scroller || !item) return;
      var sRect = scroller.getBoundingClientRect();
      var next = item.nextElementSibling;
      if (next) {
        var nRect = next.getBoundingClientRect();
        var overRight = (nRect.right + REVEAL_TRAIL) - sRect.right;
        if (overRight > 0) {
          scroller.scrollTo({ left: scroller.scrollLeft + overRight, behavior: 'smooth' });
          return;
        }
      }
      var iRect = item.getBoundingClientRect();
      if (iRect.right + 16 > sRect.right) {
        scroller.scrollTo({ left: scroller.scrollLeft + (iRect.right + 16 - sRect.right), behavior: 'smooth' });
      } else if (iRect.left < sRect.left) {
        scroller.scrollTo({ left: Math.max(0, scroller.scrollLeft - (sRect.left - iRect.left) - 16), behavior: 'smooth' });
      }
    }

    // Tapping the ALREADY-open pill must do nothing (no un-select): the theme
    // treats a primary link as a toggle, so swallow the click in the capture
    // phase before its handler — and before our own listeners below — can run.
    nav.addEventListener('click', function (evt) {
      if (!isMobile() || !evt.target.closest) return;
      var pill = evt.target.closest('.main-nav__item--primary');
      if (!pill || pill.parentElement.parentElement !== nav) return;
      var details = pill.parentElement.querySelector(':scope > details');
      if (!details) return; // plain link (e.g. Home) — let it navigate
      if (details.classList.contains('is-open') && !details.classList.contains('is-closing')) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);

    // Only one pill open at a time: opening a pill closes any other open pill.
    nav.addEventListener('click', function (evt) {
      if (!isMobile()) return;
      var pill = evt.target.closest('.main-nav__item--primary');
      if (!pill || pill.parentElement.parentElement !== nav) return;
      var currentLi = pill.parentElement;
      revealNextInScroller(nav, currentLi);
      requestAnimationFrame(function () {
        nav.querySelectorAll(':scope > li > details.is-open').forEach(function (details) {
          if (details.parentElement !== currentLi && typeof menu.close === 'function') {
            menu.close(details, true);
          }
        });
      });
      setTimeout(expandRelevantSection, 120);
    });

    // Submenu collection-tabs (e.g. Makeup's "Baza e fytyrës / Konturi …") scroll
    // horizontally too — give them the SAME reveal-next nudge. The panels are
    // relocated into the drawer body, so listen on the whole menu.
    menu.addEventListener('click', function (evt) {
      if (!isMobile() || !evt.target.closest) return;
      var tab = evt.target.closest('.main-nav__child .tablist__tab');
      if (!tab) return;
      revealNextInScroller(tab.closest('.tablist__scroller'), tab);
    });

    window.addEventListener('resize', setPillBarHeight);
    relocatePanels();
    setPillBarHeight();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();